import { useCallback, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import format from "date-fns/format";
import * as CommonTypes from "../../types/common.types";
import { useAlly } from "../../context/ally";
import AllyService from "../../services/ally.service";
import m, { desktop } from "../../styles/molanturTheme";
import { Avatar, Button, Card, CountryCode, DayStrip, Icon, IconButton, Label, TourThumb, clp, longDate } from "./ui";

// "Mis huéspedes": los pasajeros del aliado en el día elegido, agrupados por tour. Misma fuente
// que la tabla anterior (listToursTripsPassengers ya devuelve solo los pasajeros de este aliado).
export default function Guests() {
    const { currentDay, setCurrentDay, setTripInfo, setIsCreateGroupModalOpen, setIsPassengersModalOpen } = useAlly()
    const [list, setList] = useState<CommonTypes.FullTourInfo[]>([])
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
    const [open, setOpen] = useState<string>()
    const date = currentDay ? format(currentDay, 'yyyy-MM-dd') : ''

    const load = useCallback(async () => {
        if (!date) return
        setStatus('loading')
        try {
            const response = await AllyService.listTours(date)
            setList(response.data)
            setStatus('ready')
        } catch (error) {
            console.log(error)
            setStatus('error')
        }
    }, [date])

    useEffect(() => { load() }, [load])

    const tours = list
        .filter(tour => tour.reservations?.length > 0)
        .sort((a, b) => b.reservations.length - a.reservations.length)

    return (
        <Main>
            <header>
                <div>
                    <Label>{currentDay ? longDate(currentDay) : ''}</Label>
                    <h1>Tus huéspedes</h1>
                </div>
                <div className="header-actions">
                    <IconButton className="passengers-mobile" name="list" label="Mis pasajeros" tone="surface" onClick={() => setIsPassengersModalOpen(true)} />
                    <Button className="passengers-desktop" kind="ghost" icon="list" onClick={() => setIsPassengersModalOpen(true)}>Mis pasajeros</Button>
                    <Button className="add-desktop" icon="plus" onClick={() => setIsCreateGroupModalOpen(true)}>Agregar huéspedes</Button>
                </div>
            </header>

            <div className="days">
                <DayStrip day={currentDay} onChange={setCurrentDay} />
            </div>

            {status === 'loading' && (
                <div className="grid" aria-busy="true" aria-label="Cargando huéspedes">
                    <Skeleton /><Skeleton />
                </div>
            )}

            {status === 'error' && (
                <Card className="state" role="alert">
                    <strong>No pudimos cargar tus huéspedes</strong>
                    <p>Revisa tu conexión e inténtalo otra vez.</p>
                    <Button kind="ghost" onClick={load}>Reintentar</Button>
                </Card>
            )}

            {status === 'ready' && tours.length === 0 && (
                <Card className="state">
                    <span className="state-icon"><Icon name="users" size={30} stroke={1.7} /></span>
                    <strong>Aún no tienes huéspedes para este día</strong>
                    <p>Usa «Agregar huéspedes» para reservar un tour.</p>
                </Card>
            )}

            {status === 'ready' && tours.length > 0 && (
                <div className="grid">
                    {tours.map(tour => {
                        const guests = tour.reservations
                        const prices = guests.map(r => Number(r.price) || 0)
                        const total = prices.reduce((acc, p) => acc + p, 0)
                        const samePrice = prices.every(p => p === prices[0])
                        // La hora sale del trip, que solo existe cuando la agencia ya asignó la salida.
                        const times = [...new Set(guests.map(r => r.trip?.trip_time).filter(Boolean))]
                        const isOpen = open === tour.id
                        return (
                            <Card key={tour.id} $pad={0} className="tour">
                                <button type="button" className="tour-head" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? undefined : tour.id)}>
                                    <TourThumb tourId={tour.id} />
                                    <span className="tour-info">
                                        <span className="tour-name">{tour.id}</span>
                                        <span className="tour-sub">
                                            {times.length ? times.map(t => String(t).slice(0, 5)).join(' / ') : 'Hora por confirmar'}
                                            {' · '}{guests.length} {guests.length === 1 ? 'huésped' : 'huéspedes'}
                                            {samePrice ? ` · ${clp(prices[0])} c/u` : ''}
                                        </span>
                                    </span>
                                    <span className="tour-total">
                                        <span>{clp(total)}</span>
                                        <Icon name="chevronDown" size={16} stroke={2} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', marginLeft: 'auto' }} />
                                    </span>
                                </button>
                                {isOpen && (
                                    <ul className="guests">
                                        {guests.map(reservation => (
                                            <li key={reservation.id}>
                                                <Avatar name={reservation.passenger.name} />
                                                <span className="guest-name">{reservation.passenger.name}</span>
                                                <CountryCode>{reservation.passenger.nationality}</CountryCode>
                                                <button type="button" className="detail" aria-label={`Ver detalle de ${reservation.passenger.name}`} onClick={() => setTripInfo(reservation)}>
                                                    <Icon name="info" size={18} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>
                        )
                    })}
                </div>
            )}

            <div className="add-mobile">
                <Button full pill size="lg" icon="plus" onClick={() => setIsCreateGroupModalOpen(true)}>Agregar huéspedes</Button>
            </div>
        </Main>
    )
}

const pulse = keyframes`
    from { opacity: 0.55; }
    to { opacity: 1; }
`
const Skeleton = styled.div`
    height: 72px;
    border-radius: ${m.radius.lg};
    background: ${m.color.surface};
    animation: ${pulse} .8s ease-in-out infinite alternate;
`

const Main = styled.div`
    /* Espacio para el botón flotante del celular. */
    padding-bottom: 84px;

    header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 14px;
    }
    h1 {
        margin: 4px 0 0;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 26px;
        line-height: 1.1;
        color: ${m.color.ink};
    }
    .header-actions {
        display: flex;
        gap: 10px;
    }
    .add-desktop, .passengers-desktop { display: none; }
    .days { margin-bottom: 18px; }
    .grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
        gap: 12px;
        align-items: start;
    }
    .state {
        text-align: center;
        padding: 34px 20px;
        strong {
            display: block;
            margin-top: 10px;
            font-weight: 600;
            color: ${m.color.ink};
        }
        p {
            margin: 4px 0 0;
            font-size: 13px;
            color: ${m.color.inkFaint};
        }
        button { margin-top: 14px; }
    }
    .state-icon {
        display: flex;
        justify-content: center;
        color: ${m.color.inkFaint};
    }
    .tour { overflow: hidden; }
    .tour-head {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 13px;
        border: none;
        background: transparent;
        text-align: left;
        cursor: pointer;
        font-family: inherit;
        color: ${m.color.ink};
    }
    .tour-info {
        flex: 1;
        min-width: 0;
    }
    .tour-name {
        display: block;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 17px;
    }
    .tour-sub {
        display: block;
        margin-top: 2px;
        font-size: 12.5px;
        color: ${m.color.inkFaint};
    }
    .tour-total {
        text-align: right;
        font-family: ${m.font.display};
        font-weight: 600;
        font-size: 16px;
        color: ${m.color.ink};
        svg { color: ${m.color.inkFaint}; }
    }
    .guests {
        list-style: none;
        margin: 0;
        padding: 6px 13px 10px;
        border-top: 1px solid ${m.color.line};
        li {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 6px 0;
        }
    }
    .guest-name {
        flex: 1;
        min-width: 0;
        font-weight: 600;
        font-size: 14px;
    }
    .detail {
        width: 44px;
        height: 44px;
        margin: -6px -8px -6px 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: ${m.color.primaryDeep};
        cursor: pointer;
    }
    .add-mobile {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 30;
        padding: 12px 16px calc(18px + env(safe-area-inset-bottom));
        background: linear-gradient(180deg, transparent, ${m.color.bg} 38%);
    }

    ${desktop} {
        padding-bottom: 0;
        h1 { font-size: 38px; }
        header { margin-bottom: 22px; }
        .add-desktop, .passengers-desktop { display: inline-flex; }
        .add-mobile, .passengers-mobile { display: none; }
    }
`
